import { useCallback, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import * as stylex from '@stylexjs/stylex';
import {
  Button,
  Checkbox,
  IconButton,
  Label,
  Legend,
  MenuV2,
  PopoverPrimitive,
  SegmentedControl,
  Skeleton,
  Text,
  type PopoverPrimitiveOptions,
} from '@figma/fpl-components';
import {
  Icon16SeatDev,
  Icon24ArrowRight,
  Icon24AspectRatio,
  Icon24Close,
  Icon24FitToScreen,
  Icon24Minus,
  Icon24Plus,
  Icon24Sketch,
  Icon24SoundOff,
  Icon24SoundOn,
  Icon24Timer,
} from '@figma/fpl-icons';
import { ColorTheme } from '@figma/fpl-themes';
import { borderRadius, color, shadow, spacer } from '@figma/fpl-tokens/tokens.stylex';
import { I18N_KEYS } from '@/language/keys';
import { formatNumberWithCommas } from '@/utils/numbers';
import { MenuTrigger, MenuTriggerTooltip, type MenuPlacement } from './MenuTrigger';
import { MoreSettings } from './MoreSettings';
import { optionLabel } from './optionLabel';
import { SliderField } from './SliderField';
import type { GenerateModel, GenerateParam, GenerateParamValue, GeneratePill } from '@/api/generate.api';
import type { ComposerDraft, DraftPatch } from './GenerateComposer';

type ComposerFooterProps = {
  model: GenerateModel | null;
  draft: ComposerDraft | null;
  /** The plan lets this user pick Auto duration on the model. */
  canUseAutoDuration: boolean;
  signedIn: boolean;
  canGenerate: boolean;
  generating: boolean;
  /** Credits for every output of the draft. Undefined while it loads or when the server can't price it. */
  totalCost?: number;
  costLoading: boolean;
  /** The + button, for a model that takes media besides the prompt. */
  addButton: ReactNode;
  onDraftChange: (patch: DraftPatch) => void;
  onPrimaryClick: () => void;
  onCancel?: () => void;
};

/** Add button and model settings on the left; count, cost and the primary action on the right. */
export function ComposerFooter({
  model,
  draft,
  canUseAutoDuration,
  signedIn,
  canGenerate,
  generating,
  totalCost,
  costLoading,
  addButton,
  onDraftChange,
  onPrimaryClick,
  onCancel,
}: ComposerFooterProps) {
  const { t } = useTranslation();
  const primaryLabel = signedIn ? t(I18N_KEYS.GENERATE_PAGE.GENERATE) : t(I18N_KEYS.GENERATE_PAGE.SIGN_IN_TO_RUN);

  return (
    <div {...stylex.props(styles.footer)}>
      <div {...stylex.props(styles.toolbar)}>
        {addButton}
        {model && draft && (
          <ModelSettings model={model} draft={draft} canUseAutoDuration={canUseAutoDuration} onChange={onDraftChange} />
        )}
      </div>

      <div {...stylex.props(styles.actions)}>
        {model && draft && (
          <>
            <CountStepper count={draft.count} max={model.maxCount} onChange={onDraftChange} />
            {/* No number when the price can't be found: the run still goes ahead, priced by the server. */}
            {(costLoading || totalCost !== undefined) && (
              <>
                <span {...stylex.props(styles.divider)} />
                <CreditCost cost={totalCost} />
              </>
            )}
          </>
        )}
        {onCancel ? (
          <Button variant="secondary" onClick={onCancel} iconPrefix={<Icon24Close />}>
            {t(I18N_KEYS.GENERAL.CANCEL)}
          </Button>
        ) : (
          <Button
            variant="primary"
            disabled={signedIn && (!canGenerate || generating)}
            onClick={onPrimaryClick}
            iconPrefix={<Icon24ArrowRight />}
          >
            {primaryLabel}
          </Button>
        )}
      </div>
    </div>
  );
}

const PILL_ICONS: Readonly<Partial<Record<GeneratePill, ReactNode>>> = {
  resolution: <Icon24FitToScreen />,
  quality: <Icon24Sketch />,
  aspectRatio: <Icon24AspectRatio />,
  duration: <Icon24Timer />,
};

/**
 * One menu per primary `enum` param of the model's schema, an On / Off popover per primary `boolean` param,
 * and a slider popover per primary `integer` param, then the More Settings panel for the model's other settings.
 */
function ModelSettings({
  model,
  draft,
  canUseAutoDuration,
  onChange,
}: {
  model: GenerateModel;
  draft: ComposerDraft;
  canUseAutoDuration: boolean;
  onChange: (patch: DraftPatch) => void;
}) {
  const handleParamChange = useCallback(
    (key: string, value: GenerateParamValue) => onChange({ params: { ...draft.params, [key]: value } }),
    [draft.params, onChange],
  );

  return (
    <>
      {model.params.map((param) => {
        const value = draft.params[param.key];
        switch (param.type) {
          case 'boolean':
            return (
              <ParamToggle
                key={param.key}
                param={param}
                value={typeof value === 'boolean' ? value : param.defaultValue}
                placement="top span-right"
                onChange={handleParamChange}
              />
            );
          case 'integer': {
            const auto = canUseAutoDuration && param.pill === 'duration' ? param.auto : undefined;
            return (
              <ParamSlider
                key={param.key}
                param={param}
                value={typeof value === 'number' ? value : param.defaultValue}
                auto={auto}
                isAuto={auto !== undefined && value === auto}
                placement="top span-right"
                onChange={handleParamChange}
              />
            );
          }
          case 'enum':
            return (
              <ParamMenu
                key={param.key}
                param={param}
                value={typeof value === 'string' ? value : param.defaultValue}
                placement="top span-right"
                onChange={handleParamChange}
              />
            );
          default:
            return null;
        }
      })}
      {model.moreParams.length > 0 && (
        <MoreSettings
          params={model.moreParams}
          values={draft.params}
          placement={POPOVER_PLACEMENTS['top span-right']}
          onChange={handleParamChange}
        />
      )}
    </>
  );
}

function ParamMenu({
  param,
  value,
  placement,
  onChange,
}: {
  param: Extract<GenerateParam, { type: 'enum' }>;
  value: string;
  placement: MenuPlacement;
  onChange: (key: string, value: GenerateParamValue) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { manager, getTriggerProps } = MenuV2.useMenu({ open, onOpenChange: setOpen });
  const handleChange = useCallback(
    (next: string) => {
      onChange(param.key, next);
    },
    [onChange, param.key],
  );

  const valueLabel = optionLabel(param.key, value);

  return (
    <>
      <MenuTriggerTooltip title={param.title} menuOpen={open}>
        <MenuTrigger
          icon={param.pill && PILL_ICONS[param.pill]}
          {...getTriggerProps()}
          aria-label={t(I18N_KEYS.GENERATE_PAGE.PARAM_VALUE, { title: param.title, value: valueLabel })}
        >
          {valueLabel}
        </MenuTrigger>
      </MenuTriggerTooltip>
      <MenuV2.Root
        manager={manager}
        aria-label={param.title}
        position={{ area: placement }}
        colorTheme={{ brand: 'weave', mode: 'dark' }}
      >
        <MenuV2.RadioGroup title={param.title} value={value} onChange={handleChange}>
          {param.options.map((option) => (
            <MenuV2.RadioGroupItem key={option} value={option}>
              {optionLabel(param.key, option)}
            </MenuV2.RadioGroupItem>
          ))}
        </MenuV2.RadioGroup>
      </MenuV2.Root>
    </>
  );
}

const POPOVER_PLACEMENTS: Readonly<Record<MenuPlacement, PopoverPrimitiveOptions['placement']>> = {
  'top span-right': 'top-start',
  'bottom span-right': 'bottom-start',
};

/** On / Off for a primary `boolean` param, in a popover under its title. */
function ParamToggle({
  param,
  value,
  placement,
  onChange,
}: {
  param: Extract<GenerateParam, { type: 'boolean' }>;
  value: boolean;
  placement: MenuPlacement;
  onChange: (key: string, value: GenerateParamValue) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { getTriggerProps, getContainerProps } = PopoverPrimitive.usePopover({
    isOpen: open,
    onOpenChange: setOpen,
    type: 'dialog',
    softDismiss: true,
    placement: POPOVER_PLACEMENTS[placement],
    offset: 4,
  });
  const handleChange = useCallback((next: string) => onChange(param.key, next === 'on'), [onChange, param.key]);
  const valueLabel = value ? t(I18N_KEYS.GENERATE_PAGE.ON) : t(I18N_KEYS.GENERATE_PAGE.OFF);

  return (
    <>
      <MenuTriggerTooltip title={param.title} menuOpen={open}>
        <MenuTrigger
          icon={value ? <Icon24SoundOn /> : <Icon24SoundOff />}
          {...getTriggerProps()}
          aria-label={t(I18N_KEYS.GENERATE_PAGE.PARAM_VALUE, { title: param.title, value: valueLabel })}
        >
          {valueLabel}
        </MenuTrigger>
      </MenuTriggerTooltip>
      <PopoverPrimitive.Container {...getContainerProps({ 'aria-label': param.title })}>
        <ColorTheme brand="weave" mode="dark">
          <div {...stylex.props(styles.paramPopover)}>
            <SegmentedControl.Root
              legend={<Legend>{param.title}</Legend>}
              value={value ? 'on' : 'off'}
              onChange={handleChange}
            >
              <SegmentedControl.TextOption value="on" label={t(I18N_KEYS.GENERATE_PAGE.ON)} />
              <SegmentedControl.TextOption value="off" label={t(I18N_KEYS.GENERATE_PAGE.OFF)} />
            </SegmentedControl.Root>
          </div>
        </ColorTheme>
      </PopoverPrimitive.Container>
    </>
  );
}

const nearestOption = (options: readonly number[], value: number) =>
  options.reduce((best, option) => (Math.abs(option - value) < Math.abs(best - value) ? option : best));

/**
 * A slider and a number field for a primary `integer` param, in a popover under its title. When the param
 * has `options`, a value between two of them snaps to the nearest. With `auto`, an Auto checkbox hands the
 * value to the model, and unchecking it goes back to the default.
 */
function ParamSlider({
  param,
  value,
  auto,
  isAuto,
  placement,
  onChange,
}: {
  param: Extract<GenerateParam, { type: 'integer' }>;
  value: number;
  auto: string | undefined;
  isAuto: boolean;
  placement: MenuPlacement;
  onChange: (key: string, value: GenerateParamValue) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { getTriggerProps, getContainerProps } = PopoverPrimitive.usePopover({
    isOpen: open,
    onOpenChange: setOpen,
    type: 'dialog',
    softDismiss: true,
    placement: POPOVER_PLACEMENTS[placement],
    offset: 4,
  });
  const handleChange = useCallback(
    (next: number) => onChange(param.key, param.options ? nearestOption(param.options, next) : next),
    [onChange, param.key, param.options],
  );
  const handleAutoChange = useCallback(
    (checked: boolean) => onChange(param.key, checked && auto !== undefined ? auto : param.defaultValue),
    [auto, onChange, param.defaultValue, param.key],
  );
  const inSeconds = param.pill === 'duration';
  const numberLabel = inSeconds ? t(I18N_KEYS.GENERATE_PAGE.SECONDS_VALUE, { value }) : String(value);
  const valueLabel = isAuto ? t(I18N_KEYS.GENERATE_PAGE.AUTO_VALUE) : numberLabel;

  return (
    <>
      <MenuTriggerTooltip title={param.title} menuOpen={open}>
        <MenuTrigger
          icon={param.pill && PILL_ICONS[param.pill]}
          {...getTriggerProps()}
          aria-label={t(I18N_KEYS.GENERATE_PAGE.PARAM_VALUE, { title: param.title, value: valueLabel })}
        >
          {valueLabel}
        </MenuTrigger>
      </MenuTriggerTooltip>
      <PopoverPrimitive.Container {...getContainerProps({ 'aria-label': param.title })}>
        <ColorTheme brand="weave" mode="dark">
          <div {...stylex.props(styles.sliderPanel)}>
            <div {...stylex.props(styles.sliderSection)}>
              <Text.p variant="body-medium" color="secondary" {...stylex.props(styles.sliderTitle)}>
                {param.title}
              </Text.p>
              <SliderField
                label={param.title}
                value={value}
                min={param.min}
                max={param.max}
                step={1}
                bigStep={10}
                decimals={0}
                suffix={inSeconds ? t(I18N_KEYS.GENERATE_PAGE.SECONDS_UNIT) : undefined}
                disabled={isAuto}
                onChange={handleChange}
              />
              {auto !== undefined && (
                <Checkbox
                  label={<Label>{t(I18N_KEYS.GENERATE_PAGE.AUTO_DURATION)}</Label>}
                  checked={isAuto}
                  onChange={handleAutoChange}
                />
              )}
            </div>
          </div>
        </ColorTheme>
      </PopoverPrimitive.Container>
    </>
  );
}

/** Number of outputs per run, between 1 and the model's `maxCount`. */
function CountStepper({ count, max, onChange }: { count: number; max: number; onChange: (patch: DraftPatch) => void }) {
  const { t } = useTranslation();
  const handleDecrease = useCallback(() => {
    if (count > 1) onChange({ count: count - 1 });
  }, [count, onChange]);
  const handleIncrease = useCallback(() => {
    if (count < max) onChange({ count: count + 1 });
  }, [count, max, onChange]);

  return (
    <div {...stylex.props(styles.counter)} role="group" aria-label={t(I18N_KEYS.GENERATE_PAGE.COUNT)}>
      <IconButton aria-label={t(I18N_KEYS.GENERATE_PAGE.DECREASE_COUNT)} disabled={count <= 1} onClick={handleDecrease}>
        <Icon24Minus />
      </IconButton>
      <Text.span variant="body-medium" color="default" {...stylex.props(styles.count)}>
        {count}
      </Text.span>
      <IconButton
        aria-label={t(I18N_KEYS.GENERATE_PAGE.INCREASE_COUNT)}
        disabled={count >= max}
        onClick={handleIncrease}
      >
        <Icon24Plus />
      </IconButton>
    </div>
  );
}

/** A placeholder while the price loads, never the price of the settings before. */
function CreditCost({ cost }: { cost?: number }) {
  return (
    <span {...stylex.props(styles.credits)}>
      <Icon16SeatDev />
      {cost === undefined ? (
        <Skeleton.Root>
          <Skeleton.Bone width={20} height={12} borderRadius="sm" />
        </Skeleton.Root>
      ) : (
        <Text.span variant="body-medium" color="secondary">
          {formatNumberWithCommas(cost)}
        </Text.span>
      )}
    </span>
  );
}

const styles = stylex.create({
  footer: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    gap: spacer.spacer2,
    justifyContent: 'space-between',
    minHeight: '48px',
    padding: spacer.spacer25,
  },
  toolbar: {
    alignItems: 'center',
    display: 'flex',
    gap: spacer.spacer2,
    minWidth: 0,
  },
  actions: {
    alignItems: 'center',
    display: 'flex',
    gap: spacer.spacer2,
  },
  counter: {
    alignItems: 'center',
    borderColor: color.border,
    borderRadius: borderRadius.medium,
    borderStyle: 'solid',
    borderWidth: '1px',
    display: 'flex',
    gap: spacer.spacer2,
    paddingInline: spacer.spacer1,
  },
  count: {
    minWidth: '16px',
    textAlign: 'center',
  },
  divider: {
    backgroundColor: color.bordertranslucent,
    height: '20px',
    width: '1px',
  },
  credits: {
    alignItems: 'center',
    display: 'inline-flex',
    gap: spacer.spacer1,
  },
  paramPopover: {
    backgroundColor: color.bgMenu,
    borderRadius: borderRadius.large,
    boxShadow: shadow.elevation500,
    boxSizing: 'border-box',
    paddingBlock: spacer.spacer25,
    paddingInline: spacer.spacer3,
    width: '273px',
  },
  sliderPanel: {
    backgroundColor: color.bgMenu,
    borderRadius: borderRadius.large,
    boxShadow: shadow.elevation500,
    boxSizing: 'border-box',
    overflow: 'clip',
    paddingBlock: spacer.spacer2,
  },
  sliderSection: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: spacer.spacer1,
    paddingBlock: spacer.spacer1,
    paddingInline: spacer.spacer3,
    width: '241px',
  },
  sliderTitle: {
    margin: 0,
  },
});
